import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Divider,
  Paper,
  Chip,
  InputAdornment,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Google as GoogleIcon,
  Login as LoginIcon,
  MailOutline as MailIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { API_URL } from '../services/appData';
import { useAuth } from '../context/AuthContext';

const LoginPage = ({ keywordsData, language }) => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isRtl = language === 'ar';

  const goldAccent = '#E6B800';
  const softGold = 'rgba(230, 184, 0, 0.12)';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/auth/login', formData);
      if (response.data.success) {
        await login(response.data.token, response.data.user);
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setLoading(true);
    setError('');
    window.location.href = `${API_URL}/auth/google`;
  };

  const loginTxt = isRtl ? 'تسجيل الدخول' : 'Sign In';
  const processingLoginTxt = isRtl ? 'جار تسجيل الدخول...' : 'Signing in...';

  // Shared input styling
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      color: '#FFFFFF',
      borderRadius: '14px',
      fontSize: '0.95rem',
      transition: 'all 0.2s ease',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
      '&:hover fieldset': { borderColor: 'rgba(230,184,0,0.4)' },
      '&.Mui-focused fieldset': {
        borderColor: goldAccent,
        borderWidth: '1.5px',
      },
      '&.Mui-focused': {
        backgroundColor: 'rgba(230, 184, 0, 0.04)',
        boxShadow: `0 0 0 4px ${softGold}`,
      },
    },
    '& .MuiInputLabel-root': {
      color: '#8A8A8A',
      fontSize: '0.9rem',
      '&.Mui-focused': { color: goldAccent },
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#6B6B6B',
      opacity: 1,
    },
    '& .Mui-disabled': {
      backgroundColor: 'rgba(255,255,255,0.02)',
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        background: `
          radial-gradient(circle at 15% 0%, rgba(230,184,0,0.08) 0%, transparent 45%),
          radial-gradient(circle at 85% 100%, rgba(230,184,0,0.05) 0%, transparent 45%),
          #0A0A0A
        `,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 4, sm: 6 },
        px: { xs: 2, sm: 3 },
        position: 'relative',
        overflow: 'hidden',
        direction: isRtl ? 'rtl' : 'ltr',
      }}
    >
      <Container
        maxWidth="sm"
        disableGutters
        sx={{ position: 'relative', zIndex: 1 }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 4, md: 5 },
            width: '100%',
            background: 'rgba(20, 20, 20, 0.75)',
            backdropFilter: 'blur(24px) saturate(180%)',
            WebkitBackdropFilter: 'blur(24px) saturate(180%)',
            border: '1px solid rgba(230, 184, 0, 0.15)',
            borderRadius: { xs: '20px', sm: '28px' },
            boxShadow:
              '0 24px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(230,184,0,0.05)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background:
                'linear-gradient(90deg, transparent, rgba(230,184,0,0.5), transparent)',
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              textAlign: 'center',
              mb: { xs: 3, sm: 4 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* Logo */}
            <Box
              onClick={() => navigate('/')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                height: { xs: 64, sm: 74, md: 84 },
                width: { xs: 64, sm: 74, md: 84 },
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'scale(1.06)' },
              }}
            >
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 120 120"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <linearGradient
                    id="loginGoldGrad"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor="#FFD700" />
                    <stop offset="100%" stopColor="#E6A800" />
                  </linearGradient>
                  <linearGradient
                    id="loginDarkGrad"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor="#1F1F1F" />
                    <stop offset="100%" stopColor="#0D0D0D" />
                  </linearGradient>
                  <filter id="loginShadow">
                    <feDropShadow
                      dx="0"
                      dy="1"
                      stdDeviation="2"
                      floodColor="#000"
                      floodOpacity="0.5"
                    />
                  </filter>
                </defs>

                <circle
                  cx="60"
                  cy="60"
                  r="55"
                  fill="url(#loginDarkGrad)"
                  stroke="url(#loginGoldGrad)"
                  strokeWidth="3"
                />
                <circle
                  cx="60"
                  cy="60"
                  r="50"
                  fill="none"
                  stroke="#FFD700"
                  strokeWidth="0.8"
                  strokeDasharray="3 5"
                  opacity="0.2"
                />

                <ellipse
                  cx="60"
                  cy="62"
                  rx="32"
                  ry="8"
                  fill="url(#loginGoldGrad)"
                  stroke="#FFD700"
                  strokeWidth="1.5"
                  filter="url(#loginShadow)"
                />
                <rect
                  x="56"
                  y="68"
                  width="8"
                  height="16"
                  fill="url(#loginGoldGrad)"
                  rx="2"
                />
                <ellipse
                  cx="60"
                  cy="86"
                  rx="20"
                  ry="5"
                  fill="url(#loginGoldGrad)"
                  stroke="#FFD700"
                  strokeWidth="1"
                />
                <ellipse cx="60" cy="86" rx="16" ry="3.5" fill="#0D0D0D" />

                <g transform="translate(60, 52) rotate(-8)">
                  <rect
                    x="-18"
                    y="-20"
                    width="36"
                    height="36"
                    rx="3"
                    fill="#FFD700"
                    filter="url(#loginShadow)"
                  />
                  <rect x="-15" y="-17" width="7" height="7" fill="#0D0D0D" rx="1" />
                  <rect x="-13" y="-15" width="3" height="3" fill="#FFD700" />
                  <rect x="8" y="-17" width="7" height="7" fill="#0D0D0D" rx="1" />
                  <rect x="10" y="-15" width="3" height="3" fill="#FFD700" />
                  <rect x="-15" y="8" width="7" height="7" fill="#0D0D0D" rx="1" />
                  <rect x="-13" y="10" width="3" height="3" fill="#FFD700" />
                  <rect x="-6" y="-17" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-1" y="-14" width="3" height="3" fill="#0D0D0D" />
                  <rect x="4" y="-17" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-6" y="-11" width="3" height="3" fill="#0D0D0D" />
                  <rect x="0" y="-8" width="3" height="3" fill="#0D0D0D" />
                  <rect x="5" y="-11" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-15" y="-6" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-10" y="-3" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-4" y="-2" width="3" height="3" fill="#0D0D0D" />
                  <rect x="1" y="-4" width="3" height="3" fill="#0D0D0D" />
                  <rect x="7" y="-6" width="3" height="3" fill="#0D0D0D" />
                  <rect x="12" y="-3" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-15" y="1" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-10" y="4" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-4" y="2" width="3" height="3" fill="#0D0D0D" />
                  <rect x="1" y="5" width="3" height="3" fill="#0D0D0D" />
                  <rect x="7" y="3" width="3" height="3" fill="#0D0D0D" />
                  <rect x="12" y="6" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-10" y="9" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-4" y="11" width="3" height="3" fill="#0D0D0D" />
                  <rect x="1" y="9" width="3" height="3" fill="#0D0D0D" />
                  <rect x="7" y="11" width="3" height="3" fill="#0D0D0D" />
                  <rect x="12" y="9" width="3" height="3" fill="#0D0D0D" />
                </g>

                <text
                  x="42"
                  y="108"
                  textAnchor="middle"
                  fontSize="22"
                  fontWeight="900"
                  fill="#FFD700"
                  fontFamily="Arial, Helvetica, sans-serif"
                >
                  QR
                </text>
                <text
                  x="78"
                  y="108"
                  textAnchor="middle"
                  fontSize="20"
                  fontWeight="400"
                  fill="#FFFFFF"
                  fontFamily="Arial, Helvetica, sans-serif"
                >
                  table
                </text>
                <line
                  x1="30"
                  y1="114"
                  x2="90"
                  y2="114"
                  stroke="#FFD700"
                  strokeWidth="1"
                  opacity="0.5"
                />
                <circle cx="60" cy="114" r="1.5" fill="#FFD700" />
              </svg>
            </Box>

            <Typography
              variant="h5"
              sx={{
                mt: 2,
                color: '#FFFFFF',
                fontWeight: 800,
                fontSize: { xs: '1.35rem', sm: '1.5rem' },
                letterSpacing: -0.3,
              }}
            >
              {isRtl ? 'مرحباً بعودتك' : 'Welcome back'}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: '#9A9A9A',
                mt: 0.75,
                fontSize: { xs: '0.82rem', sm: '0.9rem' },
                maxWidth: 320,
              }}
            >
              {isRtl
                ? 'سجّل الدخول للمتابعة إلى حسابك'
                : 'Sign in to continue to your account'}
            </Typography>
          </Box>

          {/* Error */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2.5,
                backgroundColor: 'rgba(255, 82, 82, 0.08)',
                color: '#FF8A80',
                border: '1px solid rgba(255, 82, 82, 0.25)',
                borderRadius: '12px',
                fontSize: '0.85rem',
                '& .MuiAlert-icon': { color: '#FF5252' },
              }}
            >
              {error}
            </Alert>
          )}

          {/* Google */}
          <Button
            fullWidth
            onClick={handleGoogleLogin}
            disabled={loading}
            startIcon={<GoogleIcon sx={{ fontSize: '1.2rem !important' }} />}
            sx={{
              mb: 2.5,
              py: { xs: 1.3, sm: 1.5 },
              backgroundColor: 'rgba(255, 255, 255, 0.04)',
              color: '#FFFFFF',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '14px',
              fontWeight: 600,
              fontSize: { xs: '0.9rem', sm: '0.95rem' },
              textTransform: 'none',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                borderColor: 'rgba(230, 184, 0, 0.4)',
                transform: 'translateY(-1px)',
              },
              '&.Mui-disabled': {
                color: '#6B6B6B',
                border: '1px solid rgba(255,255,255,0.05)',
              },
            }}
          >
            {isRtl ? 'المتابعة عبر جوجل' : 'Continue with Google'}
          </Button>

          {/* Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, gap: 1.5 }}>
            <Divider sx={{ flex: 1, borderColor: 'rgba(255,255,255,0.06)' }} />
            <Typography
              sx={{
                color: '#7A7A7A',
                fontSize: '0.72rem',
                fontWeight: 600,
                letterSpacing: 1,
                textTransform: 'uppercase',
              }}
            >
              {isRtl ? 'أو' : 'or'}
            </Typography>
            <Divider sx={{ flex: 1, borderColor: 'rgba(255,255,255,0.06)' }} />
          </Box>

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={2}>
              {/* Email */}
              <TextField
                fullWidth
                label={isRtl ? 'البريد الإلكتروني' : 'Email'}
                placeholder={isRtl ? 'example@mail.com' : 'you@example.com'}
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
                autoComplete="email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailIcon sx={{ color: '#7A7A7A', fontSize: '1.15rem' }} />
                    </InputAdornment>
                  ),
                }}
                sx={inputSx}
              />

              {/* Password */}
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 0.5,
                  }}
                >
                  <Box />
                  <Button
                    component="a"
                    href="/forgot-password"
                    sx={{
                      color: '#8A8A8A',
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      p: 0,
                      minWidth: 'auto',
                      fontWeight: 500,
                      '&:hover': {
                        color: goldAccent,
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    {isRtl ? 'نسيت كلمة المرور؟' : 'Forgot password?'}
                  </Button>
                </Box>
                <TextField
                  fullWidth
                  label={isRtl ? 'كلمة المرور' : 'Password'}
                  placeholder="••••••••"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  autoComplete="current-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: '#7A7A7A', fontSize: '1.15rem' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          sx={{
                            color: '#7A7A7A',
                            '&:hover': { color: goldAccent },
                          }}
                        >
                          {showPassword ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
              </Box>

              {/* Submit */}
              <Button
                type="submit"
                fullWidth
                disabled={loading}
                startIcon={!loading && <LoginIcon />}
                sx={{
                  mt: 0.5,
                  py: { xs: 1.4, sm: 1.6 },
                  background: loading
                    ? 'rgba(255,255,255,0.06)'
                    : `linear-gradient(135deg, ${goldAccent} 0%, #FFD700 100%)`,
                  color: loading ? '#8A8A8A' : '#111',
                  fontWeight: 800,
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  letterSpacing: 0.3,
                  borderRadius: '14px',
                  textTransform: 'none',
                  boxShadow: loading
                    ? 'none'
                    : '0 8px 24px rgba(230, 184, 0, 0.28)',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    background: loading
                      ? 'rgba(255,255,255,0.06)'
                      : 'linear-gradient(135deg, #FFD700 0%, #E6B800 100%)',
                    boxShadow: loading
                      ? 'none'
                      : '0 12px 32px rgba(230, 184, 0, 0.45)',
                    transform: loading ? 'none' : 'translateY(-2px)',
                  },
                  '&.Mui-disabled': {
                    color: '#8A8A8A',
                  },
                }}
              >
                {loading ? processingLoginTxt : loginTxt}
              </Button>
            </Stack>
          </Box>

          {/* Register */}
          <Box
            sx={{
              mt: { xs: 3, sm: 4 },
              pt: 2.5,
              textAlign: 'center',
              borderTop: '1px solid rgba(255,255,255,0.05)',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#9A9A9A',
                display: 'inline',
                fontSize: '0.85rem',
              }}
            >
              {isRtl ? 'لا تمتلك حساب؟' : "Don't have an account?"}{' '}
            </Typography>
            <Button
              component="a"
              href="/register"
              sx={{
                color: goldAccent,
                fontWeight: 700,
                fontSize: '0.85rem',
                textTransform: 'none',
                minWidth: 'auto',
                p: 0.5,
                ml: isRtl ? 0 : 0.5,
                mr: isRtl ? 0.5 : 0,
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                  color: '#FFD700',
                },
              }}
            >
              {isRtl ? 'سجّل الآن' : 'Register'}
            </Button>
          </Box>
        </Paper>

        {/* Footer hint */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            color: '#5A5A5A',
            mt: 2.5,
            fontSize: '0.72rem',
          }}
        >
          {isRtl
            ? '☕ مرحباً بعودتك! استمتع بتجربة استثنائية'
            : '☕ Welcome back! Enjoy an exceptional experience'}
        </Typography>
      </Container>
    </Box>
  );
};

export default LoginPage;